import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { visibleMenu } from './navigation'
import { shell } from './strings'
import TenantLogo from '../components/TenantLogo'
import './Sidebar.css'

/** A route belongs to a menu item when it is the item or a page nested under it. */
const owns = (to, pathname) => pathname === to || pathname.startsWith(`${to}/`)

/**
 * The item that should light up: the longest `to` that owns the route. Plain
 * `owns` would mark both `/settings` and `/settings/advanced` on the nested route,
 * and `end` alone would leave `/users` dark on `/users/:id`.
 */
function activeRoute(groups, pathname) {
  let best = null
  groups.forEach((group) =>
    group.items.forEach((item) => {
      if (owns(item.to, pathname) && (!best || item.to.length > best.length)) best = item.to
    }),
  )
  return best
}

/**
 * The admin rail. Mirrors the legacy `kt_app_sidebar`: brand logo linking to the
 * statistics page, then the guarded accordion menu. Nothing else — the rail is
 * navigation, and anything that is not a destination was making it read as
 * clutter. The menu search moved to the header (MenuSearch), where there was
 * room for it, and the org/build line to the account menu.
 *
 * The legacy desktop minimize toggle is `style="display:none"`, so there is no
 * collapsed state here either — only the mobile drawer, which IS live in the
 * legacy layout via `kt_app_sidebar_mobile_toggle`.
 */
export default function Sidebar({ session, open, onClose, narrow }) {
  const { pathname } = useLocation()
  const groups = useMemo(() => visibleMenu(session), [session])

  const active = activeRoute(groups, pathname)
  const activeGroup = groups.find((group) => group.items.some((item) => item.to === active))
  /* A solo group renders as a plain link, so its id matches no accordion panel.
     That is deliberate rather than a gap: arriving at a solo destination should
     collapse whatever was open, and "open the group that owns this route" does
     exactly that when the owning group has no panel to open. */
  const activeGroupId = activeGroup?.id

  /* One group open at a time. Opening a second closed the first anyway once the
     rail was long enough to scroll, except that you had to find and close it
     yourself; letting the rail do it keeps every group's links within a screen
     of its header. */
  const [openGroup, setOpenGroup] = useState(activeGroupId ?? null)

  /* Navigation has sole authority over what is open, which is what closes a
     panel once you have chosen from it: pick a link in another section and that
     section takes the single open slot; pick a solo destination and nothing
     holds it. The section you are actually IN stays open — collapsing it would
     hide the gold "you are here" bead behind a click, and behind `inert`.

     Keyed on the resolved active LINK as well as the group, so moving between
     pages inside one section re-asserts it. Expanding a group changes no route,
     so this never fights a deliberate collapse. */
  useEffect(() => {
    if (activeGroupId) setOpenGroup(activeGroupId)
  }, [activeGroupId, active])

  /* A plain toggle, matching the reference panel's `toggleSection`. An earlier
     version also scrolled the opened group into view once its animation had run;
     it meant every expand moved the whole rail under the cursor, and the row you
     had just clicked was rarely where you left it. Opening a section should open
     a section. */
  const toggleGroup = (id) => setOpenGroup((previous) => (previous === id ? null : id))

  // Off-canvas and closed: keep the whole rail out of the tab order and the
  // accessibility tree, or Tab walks ~40 invisible links.
  const parked = narrow && !open

  return (
    <>
      <div
        className={`sidebar-scrim${open ? ' sidebar-scrim--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        id="app-sidebar"
        data-testid="sidebar"
        className={`sidebar${open ? ' sidebar--open' : ''}`}
        {...(parked ? { inert: true, 'aria-hidden': 'true' } : null)}
      >
        <div className="sidebar__brand">
          <Link to="/general" className="sidebar__logo" onClick={onClose}>
            <TenantLogo background={session?.settings?.LogoBackgroundEnabled} />
          </Link>
        </div>

        <nav className="sidebar__nav" aria-label={shell.menu}>
          {groups.map((group) => {
            const Icon = group.icon
            const expanded = openGroup === group.id
            const holdsActive = group.items.some((item) => item.to === active)

            /* A group holding one destination is not a section — it is that
               destination wearing an extra click. Six of them cost an expand
               before every visit and pushed the rest of the rail down for
               nothing. It renders as a plain link with the GROUP's icon and
               title, both of which already name the page better than the lone
               child does ("السياسات", not "قائمة السياسات"). If a licence or
               role change ever gives the group a second item, it becomes an
               accordion again on its own. */
            if (group.items.length === 1) {
              const only = group.items[0]
              return (
                <NavLink
                  key={group.id}
                  to={only.to}
                  className={`sidebar__group-link sidebar__group-link--solo${
                    only.to === active ? ' is-current' : ''
                  }`}
                  onClick={onClose}
                  data-testid="sidebar-item"
                  data-menu-id={only.id}
                >
                  <span className="sidebar__icon">
                    <Icon size={18} strokeWidth={1.75} />
                  </span>
                  <span className="sidebar__group-title">
                    <bdi>{group.title}</bdi>
                  </span>
                </NavLink>
              )
            }

            return (
              <div
                key={group.id}
                className={`sidebar__group${expanded ? ' sidebar__group--open' : ''}`}
              >
                <button
                  type="button"
                  className={`sidebar__group-link${holdsActive ? ' is-current' : ''}`}
                  aria-expanded={expanded}
                  onClick={() => toggleGroup(group.id)}
                >
                  <span className="sidebar__icon">
                    <Icon size={18} strokeWidth={1.75} />
                  </span>
                  <span className="sidebar__group-title">
                    <bdi>{group.title}</bdi>
                  </span>
                  <ChevronDown className="sidebar__chevron" size={14} strokeWidth={2.25} />
                </button>

                {/* Clipping a grid row hides the links but leaves them focusable, so
                    the collapsed panel is taken out of the tree as well. */}
                <div className="sidebar__sub" {...(expanded ? null : { inert: true })}>
                  <div className="sidebar__sub-inner">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.id}
                        to={item.to}
                        className={`sidebar__link${
                          item.to === active ? ' sidebar__link--active' : ''
                        }`}
                        onClick={onClose}
                        data-testid="sidebar-item"
                        data-menu-id={item.id}
                      >
                        <span className="sidebar__bullet" aria-hidden="true" />
                        <bdi>{item.title}</bdi>
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </nav>
      </aside>
    </>
  )
}
